import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAt,
  faCartShopping,
  faCircleUser,
  faCreditCard,
  faHashtag,
  faSignIn,
  faSignOut,
  faVoteYea,
} from "@fortawesome/free-solid-svg-icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useClerk, useUser } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { ipcRenderer, shell } from "electron";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "@youka/trpc-client";

export function Profile() {
  const trpc = useTRPC();
  const { data: balance, isLoading: isBalanceLoading } = useQuery(
    trpc.user.balance.queryOptions()
  );
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signOut } = useClerk();
  const { isSignedIn, user } = useUser();

  const [version, setVersion] = useState<string>();

  const iconClassName = "mr-2 h-4 w-4";

  useEffect(() => {
    async function init() {
      try {
        const version = await ipcRenderer.invoke("version");
        if (version) {
          setVersion(version);
        }
      } catch {}
    }
    init();
  }, []);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="flex flex-col h-full items-center">
          <FontAwesomeIcon
            icon={faCircleUser}
            className="cursor-pointer"
            size="2x"
          />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-60">
        {user?.primaryEmailAddress && (
          <DropdownMenuItem disabled>
            <FontAwesomeIcon icon={faAt} className={iconClassName} />
            {user.primaryEmailAddress.emailAddress}
          </DropdownMenuItem>
        )}
        {!isBalanceLoading && (
          <DropdownMenuItem>
            <FontAwesomeIcon icon={faCartShopping} className={iconClassName} />
            <div className="flex flex-row items-center gap-2">
              {t("Credits")}
              <span>{balance}</span>
            </div>
          </DropdownMenuItem>
        )}

        <DropdownMenuItem
          onClick={() => {
            shell.openExternal("https://store.youka.io/billing");
          }}
        >
          <FontAwesomeIcon icon={faCreditCard} className={iconClassName} />
          <span>{t("Billing")}</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={() => shell.openExternal("https://youka.canny.io/features")}
        >
          <FontAwesomeIcon icon={faVoteYea} className={iconClassName} />
          <span>{t("Feature Voting")}</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {isSignedIn ? (
          <DropdownMenuItem
            onClick={async () => {
              await signOut();
              navigate("/");
            }}
          >
            <FontAwesomeIcon icon={faSignOut} className={iconClassName} />
            <span>{t("Sign Out")}</span>
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onClick={() => {
              navigate("/");
            }}
          >
            <FontAwesomeIcon icon={faSignIn} className={iconClassName} />
            <span>{t("Sign In")}</span>
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem disabled>
          <FontAwesomeIcon icon={faHashtag} className={iconClassName} />
          {version && <span className="mr-2">{version}</span>}
          <span>{process.env.REACT_APP_GIT_SHA || "Development"}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
